"use client";

import { useState, useEffect } from "react";
import { Cart, CartItem } from "@/lib/types/cartType";
import { getProductById } from "../../static-stores/productStore";
import { getStallById } from "../../static-stores/stallStore";

// Cart storage key for localStorage
const CART_STORAGE_KEY = "retro-stall-cart";

// Utility function to generate unique cart item ID
const generateCartItemId = () => `cart-item-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

// Utility function to calculate cart totals
const calculateCartTotals = (items: CartItem[]) => {
  const subtotal = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);

  return { subtotal, totalItems };
};

// Cart Store Class
class CartStore {
  private cart: Cart | null = null;
  private listeners: Set<() => void> = new Set();

  constructor() {
    this.loadCartFromStorage();
  }

  // Load cart from localStorage
  private loadCartFromStorage() {
    if (typeof window === 'undefined') return;

    try {
      const storedCart = localStorage.getItem(CART_STORAGE_KEY);
      if (storedCart) {
        const parsedCart = JSON.parse(storedCart) as Partial<Cart>;
        // Convert date strings back to Date objects
        if (parsedCart.items && Array.isArray(parsedCart.items)) {
          parsedCart.items = parsedCart.items.map((item: unknown) => {
            const cartItem = item as Record<string, unknown>;
            return {
              ...cartItem,
              addedAt: new Date(cartItem.addedAt as string)
            } as CartItem;
          });
        }
        if (parsedCart.createdAt) {
          parsedCart.createdAt = new Date(parsedCart.createdAt);
        }
        if (parsedCart.updatedAt) {
          parsedCart.updatedAt = new Date(parsedCart.updatedAt);
        }

        this.cart = parsedCart as Cart;
      } else {
        this.initializeEmptyCart();
      }
    } catch (error) {
      console.error('Error loading cart from storage:', error);
      this.initializeEmptyCart();
    }
  }

  // Save cart to localStorage
  private saveCartToStorage() {
    if (typeof window === 'undefined' || !this.cart) return;

    try {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(this.cart));
    } catch (error) {
      console.error('Error saving cart to storage:', error);
    }
  }

  // Initialize empty cart
  private initializeEmptyCart() {
    this.cart = {
      id: `cart-${Date.now()}`,
      items: [],
      subtotal: 0,
      totalItems: 0,
      createdAt: new Date(),
      updatedAt: new Date()
    };
  }

  // Notify all listeners of cart changes
  private notifyListeners() {
    console.log('🛒 CartStore notifying listeners, cart items:', this.cart ? this.cart.items.length : 0);
    // Use setTimeout to avoid synchronous state updates that might cause hydration issues
    setTimeout(() => {
      this.listeners.forEach(listener => {
        try {
          listener();
        } catch (error) {
          console.error('Error in cart listener:', error);
        }
      });
    }, 0);
  }

  // Subscribe to cart changes
  subscribe(listener: () => void) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  // Get current cart
  getCart(): Cart {
    if (!this.cart) {
      this.initializeEmptyCart();
    }
    return this.cart!;
  }

  // Add item to cart
  addItem(productId: number, quantity: number = 1): CartItem | null {
    console.log('🛒 CartStore.addItem called with:', { productId, quantity });

    const product = getProductById(productId);
    console.log('🛒 Product lookup result:', product);

    if (!product) {
      console.error('❌ Product not found:', productId);
      return null;
    }

    const stall = getStallById(product.stall.id.toString());
    console.log('🛒 Stall lookup result:', stall);

    if (!stall) {
      console.error('❌ Stall not found:', product.stall.id);
      return null;
    }

    if (!this.cart) {
      this.initializeEmptyCart();
    }

    const cart = this.cart!;
    let addedItem: CartItem;

    // Check if item already exists in cart
    const existingItemIndex = cart.items.findIndex(item => item.productId === productId);

    if (existingItemIndex >= 0) {
      // Update quantity of existing item
      cart.items[existingItemIndex].quantity += quantity;
      addedItem = cart.items[existingItemIndex];
    } else {
      // Add new item
      const newItem: CartItem = {
        id: generateCartItemId(),
        productId,
        quantity,
        sellerId: stall.id,
        sellerName: stall.name,
        productTitle: product.title,
        productImage: product.image,
        price: parseFloat(product.price.replace('€', '')),
        condition: product.condition,
        addedAt: new Date()
      };

      cart.items.push(newItem);
      addedItem = newItem;
    }

    // Update cart totals
    const totals = calculateCartTotals(cart.items);
    cart.subtotal = totals.subtotal;
    cart.totalItems = totals.totalItems;
    cart.updatedAt = new Date();

    this.saveCartToStorage();
    this.notifyListeners();

    return addedItem;
  }

  // Update item quantity
  updateItemQuantity(itemId: string, quantity: number): boolean {
    if (!this.cart) return false;

    const cart = this.cart;
    const itemIndex = cart.items.findIndex(item => item.id === itemId);
    if (itemIndex === -1) return false;

    if (quantity <= 0) {
      // Remove item if quantity is 0 or negative
      cart.items.splice(itemIndex, 1);
    } else {
      cart.items[itemIndex].quantity = quantity;
    }

    // Update cart totals
    const totals = calculateCartTotals(cart.items);
    cart.subtotal = totals.subtotal;
    cart.totalItems = totals.totalItems;
    cart.updatedAt = new Date();

    this.saveCartToStorage();
    this.notifyListeners();

    return true;
  }

  // Remove item from cart
  removeItem(itemId: string): boolean {
    if (!this.cart) return false;

    const cart = this.cart;
    const itemIndex = cart.items.findIndex(item => item.id === itemId);
    if (itemIndex === -1) return false;

    cart.items.splice(itemIndex, 1);

    // Update cart totals
    const totals = calculateCartTotals(cart.items);
    cart.subtotal = totals.subtotal;
    cart.totalItems = totals.totalItems;
    cart.updatedAt = new Date();

    this.saveCartToStorage();
    this.notifyListeners();

    return true;
  }

  // Clear entire cart
  clearCart() {
    this.initializeEmptyCart();
    this.saveCartToStorage();
    this.notifyListeners();
  }

  // Get item count for display
  getItemCount(): number {
    return this.cart?.totalItems || 0;
  }

  // Check if cart is empty
  isEmpty(): boolean {
    return !this.cart || this.cart.items.length === 0;
  }

  // Get items grouped by seller (useful for checkout)
  getItemsBySeller(): Record<string, CartItem[]> {
    if (!this.cart) return {};

    return this.cart.items.reduce((acc, item) => {
      if (!acc[item.sellerId]) {
        acc[item.sellerId] = [];
      }
      acc[item.sellerId].push(item);
      return acc;
    }, {} as Record<string, CartItem[]>);
  }
}

// Create singleton instance
export const cartStore = new CartStore();

// Export utility functions for components
export const useCart = () => {
  const [, forceUpdate] = useState(0);

  useEffect(() => {
    const unsubscribe = cartStore.subscribe(() => {
      forceUpdate((prev: number) => prev + 1);
    });

    return () => {
      unsubscribe();
    };
  }, []);

  return {
    cart: cartStore.getCart(),
    addItem: cartStore.addItem.bind(cartStore),
    updateItemQuantity: cartStore.updateItemQuantity.bind(cartStore),
    removeItem: cartStore.removeItem.bind(cartStore),
    clearCart: cartStore.clearCart.bind(cartStore),
    getItemCount: cartStore.getItemCount.bind(cartStore),
    isEmpty: cartStore.isEmpty.bind(cartStore),
    getItemsBySeller: cartStore.getItemsBySeller.bind(cartStore),
    subscribe: cartStore.subscribe.bind(cartStore)
  };
};
